import { useState } from 'react';
import {
  Box,
  Card,
  CardActionArea,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import type { DashboardBlockLink } from '../api/dashboardApi';
import { useThemeMode } from '../../../theme/ThemeModeContext';
import { resolveApiUrl } from '../../../utils/urlUtils';

type Props = {
  links: DashboardBlockLink[];
  title: string | null;
  icon: string | null;
  backgroundColor: string | null;
  backgroundColorDarkTheme: string | null;
};

export const QuickLinkSection = ({
  links,
  title,
  icon,
  backgroundColor,
  backgroundColorDarkTheme,
}: Props) => {
  const [modalOpen, setModalOpen] = useState(false);
  const { mode } = useThemeMode();

  const cardBackgroundColor =
    mode === 'dark' ? (backgroundColorDarkTheme ?? backgroundColor) : backgroundColor;
  const bgStyle = cardBackgroundColor ? { backgroundColor: `#${cardBackgroundColor}` } : {};
  const contentColor = mode === 'dark' ? 'rgba(255, 255, 255, 0.7)' : '#fff';
  const cardSx = {
    height: '100%',
    cursor: 'pointer',
    textDecoration: 'none',
    display: 'block',
    borderRadius: '24px',
    '@media (max-width:600px)': {
      overflow: 'hidden',
      boxShadow: '0 4px 12px rgba(21, 35, 52, 0.16)',
    },
    '&:active': {
      transform: 'scale(0.98)',
    },
    ...bgStyle,
  };

  const actionSx = {
    p: 2,
    height: '100%',
    '@media (max-width:600px)': {
      p: 1.5,
      minHeight: 108,
    },
    textAlign: 'center',
  };

  if (links.length === 1) {
    return (
      <Card
        component="a"
        href={links[0]?.url ?? '#'}
        target={links[0]?.target ? '_blank' : '_self'}
        rel={links[0]?.target ? 'noopener noreferrer' : undefined}
        sx={cardSx}
      >
        <CardActionArea sx={actionSx}>
          {icon && <i className={icon} style={{ fontSize: 32, color: contentColor }} />}
          <Typography variant="subtitle1" sx={{ color: contentColor, fontWeight: 600, mt: 1 }}>
            {title}
          </Typography>
        </CardActionArea>
      </Card>
    );
  }

  // Multiple links — card opens a modal to choose
  return (
    <>
      <Card sx={cardSx} onClick={() => setModalOpen(true)}>
        <CardActionArea sx={actionSx}>
          {icon && <i className={icon} style={{ fontSize: 32, color: contentColor }} />}
          <Typography variant="subtitle1" sx={{ color: contentColor, fontWeight: 600, mt: 1 }}>
            {title}
          </Typography>
        </CardActionArea>
      </Card>

      <Dialog open={modalOpen} onClose={() => setModalOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ pr: 6 }}>
          {title}
          <IconButton
            onClick={() => setModalOpen(false)}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 0 }}>
          <List disablePadding>
            {links.map((link, idx) => (
              <ListItemButton
                key={idx}
                component="a"
                href={link.url ?? '#'}
                target={link.target ? '_blank' : '_self'}
                rel={link.target ? 'noopener noreferrer' : undefined}
                onClick={() => setModalOpen(false)}
                divider={idx < links.length - 1}
              >
                {link.imageUrl && (
                  <Box
                    component="img"
                    src={resolveApiUrl(link.imageUrl)}
                    alt=""
                    sx={{ width: 36, height: 36, objectFit: 'contain', mr: 2, flexShrink: 0 }}
                  />
                )}
                <ListItemText primary={link.title ?? link.url} />
              </ListItemButton>
            ))}
          </List>
        </DialogContent>
      </Dialog>
    </>
  );
};
